import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { SPACING } from 'Style/spacing';
import { BREAKPOINTS } from 'Style/breakpoints';
import { UI_TEXT_TYPES } from 'Style/typography';

import classNames from 'classnames';

// Utils
import { getAuthorUrl } from 'Utils/content/flipboard-urls';
import Attribution from 'Utils/content/attribution';
import sentenceCase from 'Utils/content/sentence-case';
import { USAGE_NAV_FROMS, USAGE_EVENT_NAMES } from 'Utils/analytics/usage';

// Components
import { NavFromContext } from 'ComponentLibrary/context';
import WebLink from '../../components/web-link';
import Button, { StyleVariations } from 'Webapp/shared/app/components/button';
import AuthorAvatar from '../../components/attribution/author-avatar';
import withFollowAuthor from '../../hocs/withFollowAuthor';
import withT from 'ComponentLibrary/hocs/withT';

import connector from 'Utils/connector';
import connectAmp from 'Webapp/shared/app/connectors/connectAmp';
import connectUserState from 'Webapp/shared/app/connectors/connectUserState';
import connectAuthentication from 'Webapp/shared/app/connectors/connectAuthentication';
import connectUsageSetNavFrom from 'Webapp/shared/app/connectors/connectUsageSetNavFrom';

const StoryboardAttribution = styled.div(
  UI_TEXT_TYPES.BODY_2,
  {
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '40px max-content auto',
    gap: SPACING.BASE,
  },
  BREAKPOINTS.phone({
    gridTemplateColumns: '40px auto max-content',
  }),
);

const StoryboardAttributionFollow = styled.div(
  {
    marginLeft: SPACING.LARGE,
  },
  BREAKPOINTS.phone({
    justifySelf: 'end',
  }),
);

function StoryboardAuthorAttribution({
  authorSection,
  isFollowingAuthor,
  isUserStateLoading,
  followSectionAuthor,
  unfollowSectionAuthor,
  isAmp,
  isAuthenticated,
  usageSetNavFrom,
  className,
  t,
}) {
  const author = authorSection;
  if (!author) {
    return null;
  }

  const { authorImage } = author;
  const authorProfileUrl = getAuthorUrl(author);
  const authorDisplayName = (
    <div>
      <div>{sentenceCase(t('curated_by'))}</div>
      {Attribution.getAuthorName(author)}
    </div>
  );

  const onProfileClick = () => usageSetNavFrom('storyboard-layout');
  // do not render for amp, but render if we have an author section OR
  // if the user is not authenticated (so they get the social action
  // gate, we don't need to load the author section in this latter
  // case)
  const shouldRenderFollowButton =
    !isAmp && (authorSection || !isAuthenticated);

  /**
   * NOTE: The proxied className prop is used by emotion so that this
   * component can be restyled. See storyboard-layout/storyboard-footer for an example.
   */
  const attributionClassName = classNames(className, {
    'storyboard-header__attribution': true,
  });

  return (
    <NavFromContext.Provider value={USAGE_NAV_FROMS.STORYBOARD_AUTHOR}>
      <StoryboardAttribution className={attributionClassName}>
        {authorImage !== undefined && (
          <WebLink
            href={authorProfileUrl}
            onClick={onProfileClick}
            navFromEventName={USAGE_EVENT_NAMES.SECTION_ENTER}
          >
            <AuthorAvatar author={author} />
          </WebLink>
        )}
        <WebLink
          href={authorProfileUrl}
          onClick={onProfileClick}
          navFromEventName={USAGE_EVENT_NAMES.SECTION_ENTER}
        >
          {authorDisplayName}
        </WebLink>
        {shouldRenderFollowButton && (
          <StoryboardAttributionFollow>
            {isFollowingAuthor && unfollowSectionAuthor && (
              <Button
                name="storyboard-author-unfollow"
                label={t('unfollow')}
                styleVariation={StyleVariations.PRIMARY}
                disabled={isUserStateLoading}
                onClick={unfollowSectionAuthor}
              />
            )}
            {!isFollowingAuthor && followSectionAuthor && (
              <Button
                name="storyboard-author-follow"
                label={t('follow')}
                styleVariation={StyleVariations.PRIMARY}
                disabled={isUserStateLoading}
                onClick={followSectionAuthor}
              />
            )}
          </StoryboardAttributionFollow>
        )}
      </StoryboardAttribution>
    </NavFromContext.Provider>
  );
}

StoryboardAuthorAttribution.propTypes = {
  authorSection: PropTypes.object,
  isUserStateLoading: PropTypes.bool.isRequired,
  isFollowingAuthor: PropTypes.bool.isRequired,
  followSectionAuthor: PropTypes.func,
  unfollowSectionAuthor: PropTypes.func,
  isAmp: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  usageSetNavFrom: PropTypes.func.isRequired,
  className: PropTypes.string,
  t: PropTypes.func.isRequired,
};

StoryboardAuthorAttribution.defaultProps = {
  authorSection: null,
  coverItem: null,
  followSectionAuthor: null,
  unfollowSectionAuthor: null,
};

export default connector(
  connectUsageSetNavFrom,
  connectUserState,
  connectAuthentication,
  connectAmp,
)(withT(withFollowAuthor(StoryboardAuthorAttribution)));
